import {
  Card,
  Col,
  DatePicker,
  Empty,
  Input,
  Row,
  Select,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { auditApi, type AuthEvent, type AuthEventType } from '@/api/audit';
import { PageHeader } from '@/shared/components/page-header';
import { ExportButton } from '@/shared/components/ui';

const TYPE_COLOR: Record<AuthEventType, string> = {
  LOGIN_SUCCESS: 'green',
  LOGIN_FAILED: 'red',
  LOGOUT: 'default',
  SESSION_EXPIRED: 'orange',
};

const TYPE_LABEL: Record<AuthEventType, string> = {
  LOGIN_SUCCESS: 'Login',
  LOGIN_FAILED: 'Login failed',
  LOGOUT: 'Logout',
  SESSION_EXPIRED: 'Session expired',
};

export function AuthEventsPage() {
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().subtract(7, 'day'), dayjs()]);
  const [username, setUsername] = useState('');
  const [eventType, setEventType] = useState<AuthEventType | undefined>();
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(50);

  const params = {
    username: username.trim() || undefined,
    eventType,
    from: range[0].format('YYYY-MM-DD'),
    to: range[1].format('YYYY-MM-DD'),
    page,
    size,
  };

  const events = useQuery({
    queryKey: ['audit', 'auth-events', params],
    queryFn: () => auditApi.authEvents(params),
  });

  const summary = useQuery({
    queryKey: ['audit', 'auth-events', 'summary', params.from, params.to],
    queryFn: () => auditApi.authEventSummary(params.from, params.to),
  });

  const columns: TableProps<AuthEvent>['columns'] = [
    {
      title: 'When',
      dataIndex: 'occurredAt',
      width: 180,
      render: (v: string) => dayjs(v).format('D MMM YYYY HH:mm:ss'),
    },
    {
      title: 'User',
      dataIndex: 'username',
      width: 160,
      render: (v: string, row) => (
        <div>
          <Typography.Text strong>{v}</Typography.Text>
          {!row.appUserId && (
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                no matching account
              </Typography.Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Event',
      dataIndex: 'eventType',
      width: 160,
      render: (v: AuthEventType) => <Tag color={TYPE_COLOR[v]}>{TYPE_LABEL[v]}</Tag>,
    },
    {
      title: 'IP',
      dataIndex: 'ipAddress',
      width: 160,
      render: (v: string | null) => v ?? <Typography.Text type="secondary">—</Typography.Text>,
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <PageHeader
        eyebrow="Audit"
        title="Login activity"
        description="Every login, logout, failed-attempt, and expired session. Append-only — entries can never be edited or deleted."
        actions={
          <ExportButton
            items={[{
              kind: 'xlsx',
              url: '/reports/exports/auth-events.xlsx',
              params: {
                username: params.username,
                eventType: params.eventType,
                from: params.from,
                to: params.to,
              },
              fileName: `auth-events-${params.from}-to-${params.to}.xlsx`,
            }]}
          />
        }
      />

      <Card>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={9}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Period
            </Typography.Text>
            <DatePicker.RangePicker
              style={{ width: '100%' }}
              value={range}
              onChange={(v) => {
                if (v && v[0] && v[1]) {
                  setRange([v[0], v[1]]);
                  setPage(0);
                }
              }}
              allowClear={false}
            />
          </Col>
          <Col xs={24} md={6}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Username
            </Typography.Text>
            <Input.Search
              placeholder="Filter by username (partial match)"
              allowClear
              onSearch={(v) => {
                setUsername(v);
                setPage(0);
              }}
              onChange={(e) => setUsername(e.target.value)}
              value={username}
            />
          </Col>
          <Col xs={24} md={5}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Event type
            </Typography.Text>
            <Select<AuthEventType | undefined>
              style={{ width: '100%' }}
              placeholder="All events"
              allowClear
              value={eventType}
              onChange={(v) => {
                setEventType(v ?? undefined);
                setPage(0);
              }}
              options={(['LOGIN_SUCCESS', 'LOGIN_FAILED', 'LOGOUT', 'SESSION_EXPIRED'] as AuthEventType[]).map((v) => ({
                value: v,
                label: TYPE_LABEL[v],
              }))}
            />
          </Col>
        </Row>
      </Card>

      {summary.data && (
        <Card>
          <Row gutter={[16, 12]}>
            <Col xs={12} md={5}>
              <Statistic title="Total" value={summary.data.total} />
            </Col>
            <Col xs={12} md={5}>
              <Statistic title="Logins" value={summary.data.loginSuccess} styles={{ content: { color: '#389e0d' } }} />
            </Col>
            <Col xs={12} md={5}>
              <Statistic
                title="Failed attempts"
                value={summary.data.loginFailed}
                styles={{ content: { color: summary.data.loginFailed > 0 ? '#cf1322' : undefined } }}
              />
            </Col>
            <Col xs={12} md={5}>
              <Statistic title="Logouts" value={summary.data.logout} />
            </Col>
            <Col xs={12} md={4}>
              <Statistic title="Session expired" value={summary.data.sessionExpired} />
            </Col>
          </Row>
        </Card>
      )}

      <Card>
        {events.data && events.data.content.length === 0 ? (
          <Empty description="No events match the current filters." />
        ) : (
          <Table<AuthEvent>
            rowKey="id"
            dataSource={events.data?.content ?? []}
            loading={events.isFetching}
            columns={columns}
            pagination={{
              current: page + 1,
              pageSize: size,
              total: events.data?.totalElements ?? 0,
              showSizeChanger: true,
              pageSizeOptions: [25, 50, 100, 200],
              onChange: (p, s) => {
                setPage(p - 1);
                if (s !== size) setSize(s);
              },
            }}
            size="small"
          />
        )}
      </Card>
    </div>
  );
}
